import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Tiles, ContainerControlSite, Content, Modal, TextInput, Button, Text } from '@simplybenefits/sb-ui-library';
import Header from '../../../components/Header.js';
import permissionLevel from '../../../services/permissions.js';
import DentalClaim from './Claim/index.js';
import COB from './COB/index.js';
import Predetermination from './Predetermination/index.js';
import PendingManualTransactions from './PendingManualTransactions/index.js';
import DentalData from './DentalData/index.js';
import SubmitClaimModal from './Sections/SubmitTransactionModal.js';
import ElectronicSubmissionRequests from './ElectronicSubmissionRequests/index.js';

class Dental extends PureComponent {
    state = {
        selectedType: null,
        showSubmitClaimModal: false,
        pendingCreateCert: null,
        showCreateTransactionModal: false,
        createCert: '',
        createType: 'Claim',
        createCertError: null,
        showCreateManualConfirmation: false
    };

    componentDidMount() {
        if (this.props.match.params.type) {
            this.setState({
                selectedType: this.props.match.params.type
            });
        }
    }

    static getDerivedStateFromProps(props) {
        if (props.accessType && !permissionLevel('Adjudication', props.accessType).includes('write')) {
            window.location.href = '/403';
        }

        return null;
    }

    _setType = (type) => {
        this.setState({
            selectedType: type
        });
    };

    _buildBreadCrumbs = () => {
        if ([ 'pendingManual', 'dentalData', 'claim', 'cob', 'predetermination', 'electronicSubmissionRequests' ].includes(this.state.selectedType)) {
            return [
                {
                    title: 'Adjudication',
                    link: `/adjudication`
                },
                {
                    title: `Simply Dental`,
                    onClick: () => this._setType(null)
                }
            ];
        }

        return [
            {
                title: 'Adjudication',
                link: `/adjudication`
            }
        ];
    };

    _getSubHeader = () => {
        if (this.state.selectedType === 'pendingManual') {
            return "Pending Transactions";
        } else if (this.state.selectedType === 'dentalData') {
            return "Dental Data";
        } else if (this.state.selectedType === 'claim') {
            return "Submit First Payor Claim";
        } else if (this.state.selectedType === 'cob') {
            return "Submit Coordinated Claim";
        } else if (this.state.selectedType === 'predetermination') {
            return "Submit Predetermination";
        } else if (this.state.selectedType === 'electronicSubmissionRequests') {
            return "Paper Dental Claims";
        }

        return "Simply Dental";
    };

    _handleSubmitClaim = () => {
        this.setState({ showSubmitClaimModal: false });
    };

    _handleCreateTransaction = () => {
        const { createCert, createType } = this.state;

        if (!createCert || !createCert.trim()) {
            this.setState({ createCertError: 'Certificate number is required.' });
            return;
        }

        const typeMap = {
            'Claim': 'claim',
            'COB': 'cob',
            'Predetermination': 'predetermination'
        };

        this.setState({
            showCreateTransactionModal: false,
            createCert: '',
            createType: 'Claim',
            createCertError: null,
            selectedType: typeMap[createType],
            pendingCreateCert: createCert.trim()
        });
    };

    render() {
        const tiles = [
            {
                title: 'Submit Transaction',
                description: 'Send a dental transaction through the automated dental adjudication system.',
                onClick: () => this.setState({ showSubmitClaimModal: true }),
                icon: 'Create Claim'
            },
            {
                title: 'Manually Enter Transaction',
                description: 'Manually enter and create a new claim, COB, or predetermination transaction.',
                onClick: () => this.setState({ showCreateManualConfirmation: true }),
                icon: 'Create Claim'
            },
            {
                title: 'Pending Transactions',
                description: 'View a list of all flagged dental transactions pending review.',
                isSelected: this.state.selectedType === 'pendingManual',
                onClick: () => this._setType('pendingManual'),
                icon: 'Claims History'
            },
            {
                title: 'Dental Data',
                description: 'View dental codes and payees data.',
                isSelected: this.state.selectedType === 'dentalData',
                onClick: () => this._setType('dentalData'),
                icon: 'Dental Data'
            }
        ];

        if (permissionLevel('Electronic Submission Requests', this.props.accessType).includes('read')) {
            tiles.push({
                title: 'Paper Dental Claims',
                description: 'View and upload paper dental claims and request for electronic submission.',
                isSelected: this.state.selectedType === 'electronicSubmissionRequests',
                onClick: () => this._setType('electronicSubmissionRequests'),
                icon: 'Receipt'
            });
        }

        return (
            <ContainerControlSite background={"true"}>
                <Header
                    subHeader={ this._getSubHeader() }
                    breadcrumbs={ this._buildBreadCrumbs() }
                    selectedTitle="adjudication"
                />

                <Content>
                    {
                        ![ 'pendingManual', 'dentalData', 'claim', 'cob', 'predetermination', 'electronicSubmissionRequests' ].includes(this.state.selectedType) &&
                            <Tiles tiles={tiles} />
                    }

                    {
                        this.state.selectedType === 'claim' &&
                            <DentalClaim
                                close={ () => this.setState({ selectedType: null, pendingCreateCert: null }) }
                                selectedCert={this.state.pendingCreateCert || this.props.match.params.cert}
                                selectedClaimID={this.props.match.params.claimID}
                                manualTransactionUid={this.props.match.params.manualTransactionUid}
                            />
                    }

                    {
                        this.state.selectedType === 'cob' &&
                            <COB
                                close={ () => this.setState({ selectedType: null, pendingCreateCert: null }) }
                                selectedCert={this.state.pendingCreateCert || this.props.match.params.cert}
                                selectedClaimID={this.props.match.params.claimID}
                                manualTransactionUid={this.props.match.params.manualTransactionUid}
                            />
                    }

                    {
                        this.state.selectedType === 'predetermination' &&
                            <Predetermination
                                close={ () => this.setState({ selectedType: null, pendingCreateCert: null }) }
                                selectedCert={this.state.pendingCreateCert || this.props.match.params.cert}
                                selectedClaimID={this.props.match.params.claimID}
                                manualTransactionUid={this.props.match.params.manualTransactionUid}
                            />
                    }

                    {
                        this.state.selectedType === 'pendingManual' &&
                            <PendingManualTransactions
                                close={ () => this._setType(null) }
                            />
                    }

                    {
                        this.state.selectedType === 'dentalData' &&
                            <DentalData />
                    }

                    {
                        this.state.selectedType === 'electronicSubmissionRequests' &&
                            <ElectronicSubmissionRequests
                                accessType={ this.props.accessType }

                            />
                    }
                </Content>

                {
                    this.state.showSubmitClaimModal &&
                        <SubmitClaimModal
                            onClose={ () => this.setState({ showSubmitClaimModal: false }) }
                            onSubmit={ this._handleSubmitClaim }
                        />
                }

                <Modal
                    open={this.state.showCreateManualConfirmation}
                    onClose={() => {
                        this.setState({
                            showCreateManualConfirmation: false,
                            showCreateTransactionModal: false,
                            createCert: '',
                            createType: 'Claim',
                            createCertError: null
                        });
                    }}
                    title="Are you sure?"
                    primaryButton={{
                        label: "Continue",
                        onClick: () => {
                            this.setState({
                                showCreateManualConfirmation: false,
                                showCreateTransactionModal: true
                            });
                        }
                    }}
                    loader={ this.props.loader }
                >
                    <Text medium margin="10px 0 0 0">
                        Are you sure you want to start from scratch with this dental transaction? This should really only be used when no documents are available.
                    </Text>
                </Modal>
                <Modal
                    open={this.state.showCreateTransactionModal}
                    onClose={() => {
                        this.setState({
                            showCreateTransactionModal: false,
                            createCert: '',
                            createType: 'Claim',
                            createCertError: null
                        });
                    }}
                    title="Submit Manual Transaction"
                >
                    <>
                        <TextInput
                            label="Certificate Number"
                            fullWidth
                            value={ this.state.createCert }
                            variant="outlined"
                            onChange={(e) => this.setState({ createCert: e.target.value, createCertError: null })}
                            error={ Boolean(this.state.createCertError) }
                            helperText={ this.state.createCertError }
                        />

                        <TextInput
                            label="Transaction Type"
                            fullWidth
                            value={ this.state.createType }
                            variant="outlined"
                            onChange={(e) => this.setState({ createType: e.target.value })}
                            select
                            selectOptions={[
                                'Claim',
                                'COB',
                                'Predetermination'
                            ]}
                            style={{ marginTop: 16 }}
                        />

                        <Button
                            onClick={ this._handleCreateTransaction }
                            marginTop={2.5}
                            color="primary"
                            fullWidth
                        >
                            Continue
                        </Button>
                    </>
                </Modal>
            </ContainerControlSite>
        );
    }
}

Dental.propTypes = {
    master: PropTypes.object,
    accessType: PropTypes.string
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    master: state.master,
    accessType: state.master.access
});

export default connect(mapStateToProps)(Dental);